<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (only for dashboard permission) -->
        <button v-if="hasSuperPermission('employment-requests-dashboard')" @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter && hasSuperPermission('employment-requests-dashboard')"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2
                        class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1 uppercase">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6 text-left">
                    <!-- Looking for Job Filter -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Looking for Job</label>
                        <select v-model="filters.lookingForJob"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm">
                            <option value="">All</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </select>
                    </div>

                    <!-- Location Filter -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Location</label>
                        <multiselect v-model="filters.selectedLocations" :options="availableLocations" :multiple="true"
                            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                            placeholder="Select locations" label="label" track-by="value" class="w-full">
                        </multiselect>
                    </div>

                    <!-- Employer Filter -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Employer</label>
                        <multiselect v-model="filters.selectedEmployers" :options="availableEmployers" :multiple="true"
                            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
                            :custom-label="partnerLabel" track-by="id" placeholder="Select employers" class="w-full">
                            <template #noResult>
                                <span class="text-sm text-gray-500">No employers found</span>
                            </template>
                            <template #noOptions>
                                <span class="text-sm text-gray-500">No employers available</span>
                            </template>
                        </multiselect>
                    </div>

                    <!-- Entity Filter -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filters.entity"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm">
                            <option value="">All</option>
                            <option v-for="item in entitiesList" :key="item.id" :value="item.entity">
                                {{ getEntityDisplayName(item.entity) }}
                            </option>
                        </select>
                    </div>

                    <!-- Campus Filter -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
                        <select v-model="filters.campus"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm">
                            <option value="">All</option>
                            <option v-for="c in filteredCampuses" :key="c.campus_code" :value="c.campus_code">
                                {{ c.campus_code }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Apply / Clear Buttons -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold rounded-lg">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-lg">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="h-full container mx-auto">
            <div class="flex flex-col h-full w-full p-0 sm:p-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <div>
                        <router-link to="/employment"
                            class="hover:text-blue-700 transition font-medium">Employment</router-link>
                        <span class="mx-2">/</span>
                    </div>
                    <span class="text-blue-900 font-semibold">Employment Requests</span>
                </nav>

                <!-- Header -->
                <div class="header-section bg-white shadow-sm rounded-lg p-6 mb-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h1 class="text-2xl font-bold text-gray-900 mb-2">Employment Requests</h1>
                            <p class="text-gray-600">Submit and manage your job search requests</p>
                        </div>
                        <div class="flex space-x-3">
                            <div v-if="role != 'user'">
                                <div v-if="isExportExcel"
                                    class="inline-flex items-center px-4 py-2 rounded-lg bg-gray-300 text-gray-600 cursor-not-allowed">
                                    <base-spinner-with-text class-list="w-4 h-4" text="" text-class-list="text-gray-600" />
                                    <span class="ml-2 text-sm">Exporting Excel</span>
                                </div>
                                <div v-else @click="generateReport"
                                    class="inline-flex items-center px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white cursor-pointer transition-colors">
                                    <font-awesome-icon :icon="['fas', 'file-excel']" />
                                    <span class="ml-2 text-sm">Export Excel</span>
                                </div>
                            </div>
                            <base-button
                                v-if="hasSuperPermission('employment-requests-create') && !hasExistingRequestForCurrentUser"
                                @click="showCreateModal = true" class="bg-blue-600 hover:bg-blue-700 text-white">
                                <font-awesome-icon icon="plus" class="mr-2" />
                                Create Request
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- Dashboard Analytics Tiles (only for dashboard permission) -->
                <div v-if="hasSuperPermission('employment-requests-dashboard')"
                    class="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                    <!-- Total Requests -->
                    <div
                        class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col justify-between hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center justify-between mb-3">
                            <h3 class="text-sm font-semibold text-gray-700">Total Requests</h3>
                            <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <font-awesome-icon icon="briefcase" class="text-blue-600 text-sm" />
                            </div>
                        </div>
                        <div class="text-3xl font-bold text-gray-900">
                            {{ dashboardStats.totalRequests }}
                        </div>
                    </div>

                    <!-- Looking for Job -->
                    <div
                        class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col justify-between hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center justify-between mb-3">
                            <h3 class="text-sm font-semibold text-gray-700">Looking for Job</h3>
                            <div class="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                <font-awesome-icon icon="user-check" class="text-green-600 text-sm" />
                            </div>
                        </div>
                        <div class="text-3xl font-bold text-gray-900">
                            {{ dashboardStats.lookingForJobCount }}
                        </div>
                    </div>

                    <!-- Not Looking for Job -->
                    <div
                        class="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col justify-between hover:shadow-md transition-shadow duration-200">
                        <div class="flex items-center justify-between mb-3">
                            <h3 class="text-sm font-semibold text-gray-700">Not Looking for Job</h3>
                            <div class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                                <font-awesome-icon icon="user-times" class="text-gray-600 text-sm" />
                            </div>
                        </div>
                        <div class="text-3xl font-bold text-gray-900">
                            {{ dashboardStats.notLookingForJobCount }}
                        </div>
                    </div>
                </div>

                <!-- Employment Requests List -->
                <div class="bg-white shadow-sm rounded-lg flex-auto flex flex-col">
                    <div class="p-6 border-b border-gray-200">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                            <h2 class="text-lg font-semibold text-gray-900">All Requests</h2>
                            <div class="flex items-center gap-4">
                                <search @on-search="onSearch" class-list="px-4 h-8"></search>
                            </div>
                        </div>
                    </div>

                    <!-- Loading State -->
                    <div class="flex-auto flex flex-col" v-if="loading">
                        <table-shimmer :titleHeaders="[
                            '#',
                            'Student',
                            'Looking for Job',
                            'Preferred Location',
                            'Expected Salary',
                            'Preferred Employers',
                            'Created',
                            'Actions',
                        ]" :rows="first"></table-shimmer>
                    </div>

                    <!-- Empty State -->
                    <div v-else-if="!loading && requestsList.length === 0"
                        class="flex-auto flex justify-center items-center py-12">
                        <div class="text-center max-w-md">
                            <div class="mb-6">
                                <div
                                    class="mx-auto w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                                    <font-awesome-icon icon="briefcase" class="h-12 w-12 text-blue-500" />
                                </div>
                            </div>
                            <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Requests Found</h2>
                            <p v-if="role == 'user'" class="text-gray-500 mb-6 leading-relaxed">
                                You haven't created any employment requests yet. Click the button below to create your
                                first
                                request.
                            </p>
                            <button
                                v-if="hasSuperPermission('employment-requests-create') && !hasExistingRequestForCurrentUser"
                                @click="showCreateModal = true"
                                class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 text-sm font-medium shadow-lg">
                                <font-awesome-icon icon="plus" class="h-4 w-4 mr-2" />
                                Create First Request
                            </button>
                        </div>
                    </div>

                    <!-- Requests Table -->
                    <div v-else class="flex flex-col flex-auto">
                        <div class="flex-auto flex flex-col overflow-x-auto">
                            <div class="inline-block min-w-full py-2">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-left text-sm font-light">
                                        <thead class="border-b font-medium dark:border-neutral-500">
                                            <tr>
                                                <th scope="col" class="px-6 py-2 text-gray-400 w-8">
                                                    #
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Student
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Looking for Job
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Preferred Location
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Expected Salary
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Preferred Employers
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Created
                                                </th>
                                                <th scope="col" class="px-6 py-2 text-gray-400">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(request, index) in requestsList" :key="request.id"
                                                class="border-b border-dashed dark:border-neutral-500 hover:bg-gray-50">
                                                <td class="whitespace-nowrap px-6 py-2 font-medium">
                                                    {{ paginatorInfoWithItems ? paginatorInfoWithItems.firstItem + index
                                                        : index + 1 }}
                                                </td>
                                                <td class="whitespace-nowrap px-6 py-2">
                                                    <div class="flex items-center">
                                                        <div class="flex-shrink-0 mr-3">
                                                            <img v-if="request.user && request.user.profile_picture_url"
                                                                @click.stop="openPreviewImgModal(request.user.profile_picture_url, request.student_id)"
                                                                class="h-12 w-12 rounded-lg object-cover cursor-pointer"
                                                                :src="getProfilePicture(request.user.profile_picture_url, request.student_id)"
                                                                alt="" />
                                                            <div v-else
                                                                class="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                                                <font-awesome-icon :icon="['fas', 'user']"
                                                                    class="text-gray-400 text-xl" />
                                                            </div>
                                                        </div>
                                                        <div class="flex-grow flex flex-col">
                                                            <h1 class="text-sm font-semibold flex items-center">
                                                                <span v-if="role != 'user' && request.student_id"
                                                                    @click.stop="viewStudentProfile(request.student_id)"
                                                                    class="text-heading hover:text-blue-600 hover:underline cursor-pointer">{{ request.user ?
                                                                    request.user.name : 'N/A' }}</span>
                                                                <span v-else class="text-heading">{{ request.user ?
                                                                    request.user.name : 'N/A' }}</span>
                                                            </h1>
                                                            <p class="text-xs text-gray-400">
                                                                {{ request.user ? request.user.email : 'N/A' }}
                                                            </p>
                                                            <p class="text-xs text-gray-400">
                                                                {{ request.student_id || 'N/A' }}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="px-6 py-2">
                                                    <span v-if="request.is_looking_for_job"
                                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                        Yes
                                                    </span>
                                                    <span v-else
                                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                                        No
                                                    </span>
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ request.preferred_location || 'N/A' }}
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ request.expected_salary || 'N/A' }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div v-if="request.partners && request.partners.length > 0"
                                                        class="flex flex-wrap gap-1">
                                                        <span v-for="partner in request.partners.slice(0, 2)"
                                                            :key="partner.id"
                                                            class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                            {{ partner.en_company_name || partner.company_short_name }}
                                                        </span>
                                                        <span v-if="request.partners.length > 2"
                                                            class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                                            +{{ request.partners.length - 2 }} more
                                                        </span>
                                                    </div>
                                                    <span v-else class="text-gray-500">N/A</span>
                                                </td>
                                                <td class="px-6 py-2 text-gray-900">
                                                    {{ formatDate(request.created_at) }}
                                                </td>
                                                <td class="px-6 py-2">
                                                    <div class="flex">
                                                        <div @click.stop="viewRequest(request)"
                                                            class="bg-gray-100 hover:bg-blue-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="View Details">
                                                            <icon-eye
                                                                class="w-4 h-4 transform group-hover:text-purple-pink group-hover:scale-110"></icon-eye>
                                                        </div>
                                                        <div v-if="role != 'user' && request.student_id"
                                                            @click.stop="viewStudentProfile(request.student_id)"
                                                            class="bg-gray-100 hover:bg-green-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="View Student Profile">
                                                            <font-awesome-icon :icon="['fas', 'user']"
                                                                class="w-4 h-4 transform group-hover:text-green-600 group-hover:scale-110" />
                                                        </div>
                                                        <div v-if="canUpdateRequest(request)"
                                                            @click.stop="editRequest(request)"
                                                            class="bg-gray-100 hover:bg-indigo-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="Edit">
                                                            <icon-pen
                                                                class="w-4 h-4 transform group-hover:text-indigo-600 group-hover:scale-110"></icon-pen>
                                                        </div>
                                                        <div v-if="canDeleteRequest(request)"
                                                            @click.stop="deleteRequest(request)"
                                                            class="bg-gray-100 hover:bg-red-50 w-8 h-8 flex justify-center items-center rounded-lg group cursor-pointer mr-2"
                                                            title="Delete">
                                                            <icon-trash
                                                                class="w-4 h-4 transform group-hover:text-red-600 group-hover:scale-110"></icon-trash>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div class="mt-4 px-6 pb-6"
                            v-if="paginatorInfoWithItems && paginatorInfoWithItems.lastPage > 1">
                            <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Create/Edit Request Modal -->
        <transition name="fade" mode="out-in">
            <base-modal v-if="showCreateModal || editingRequest"
                :title="editingRequest ? 'Edit Employment Request' : 'Create Employment Request'" @close="closeModal">
                <employment-request-form-modal :request="editingRequest" @close="closeModal"
                    @saved="handleRequestSaved">
                </employment-request-form-modal>
            </base-modal>
        </transition>

        <!-- View Request Modal -->
        <transition name="fade" mode="out-in">
            <base-modal v-if="viewingRequest" :title="'Employment Request Details'" @close="closeViewModal">
                <div class="text-left p-6">
                    <div class="space-y-4">
                        <div>
                            <label class="text-sm font-semibold text-gray-700">Looking for Job</label>
                            <p class="mt-1">
                                <span v-if="viewingRequest.is_looking_for_job"
                                    class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Yes
                                </span>
                                <span v-else
                                    class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                                    No
                                </span>
                            </p>
                        </div>
                        <div v-if="viewingRequest.is_looking_for_job" class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Academic Year</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.academic_year || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Semester</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.semester || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Preferred Location</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.preferred_location || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Expected Salary</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.expected_salary || 'N/A' }}</p>
                            </div>
                            <div v-if="viewingRequest.partners && viewingRequest.partners.length > 0"
                                class="col-span-2">
                                <label class="text-sm font-semibold text-gray-700">Preferred Employers</label>
                                <div class="mt-1 flex flex-wrap gap-2">
                                    <span v-for="partner in viewingRequest.partners" :key="partner.id"
                                        class="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {{ partner.en_company_name || partner.company_short_name }}
                                    </span>
                                </div>
                            </div>
                            <div v-if="viewingRequest.additional_notes" class="col-span-2">
                                <label class="text-sm font-semibold text-gray-700">Additional Notes</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.additional_notes }}</p>
                            </div>
                        </div>
                        <div v-if="!viewingRequest.is_looking_for_job && viewingRequest.student && (viewingRequest.student.employer || (viewingRequest.student.employment_informations && viewingRequest.student.employment_informations.employer))"
                            class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Employment Status</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student.current_status || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Employer</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student.employer ||
                                    (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.employer) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Sector</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student.sector ||
                                    (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.sector) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Location</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.location) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Position</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student.emp_pos ||
                                    (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.position) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Salary Range</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.salary_range) ||
                                    viewingRequest.student.salary_range || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Job Offers</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student.job_offers_count || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Interviews</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student.interviews_count || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Got Job Within</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.jobcat) ||
                                    viewingRequest.student.jobcat ||
                                    'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Job Related to Major</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.is_job_rel_major === true) ? 'Yes' :
                                    (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.is_job_rel_major === false) ? 'No' :
                                    'N/A' }}
                                </p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Contract Type</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.contract_type) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Start Date</label>
                                <p class="mt-1 text-gray-900">{{
                                    formatDate((viewingRequest.student.employment_informations &&
                                        viewingRequest.student.employment_informations.start_date) ||
                                    viewingRequest.student.start_date) || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">End Date</label>
                                <p class="mt-1 text-gray-900">{{
                                    formatDate((viewingRequest.student.employment_informations &&
                                        viewingRequest.student.employment_informations.end_date) ||
                                    viewingRequest.student.end_date)
                                    || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Currently Working Here</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.is_working) ? 'Yes' :
                                    viewingRequest.student.employment_informations ? 'No' : 'N/A' }}</p>
                            </div>
                            <div class="col-span-2">
                                <label class="text-sm font-semibold text-gray-700">How did you get this job</label>
                                <p class="mt-1 text-gray-900">{{ (viewingRequest.student.employment_informations &&
                                    viewingRequest.student.employment_informations.job_resource) || 'N/A' }}{{
                                        (viewingRequest.student.employment_informations &&
                                            viewingRequest.student.employment_informations.job_resource === 'Other' &&
                                            viewingRequest.student.employment_informations.job_resource_other) ? ' (' +
                                    viewingRequest.student.employment_informations.job_resource_other + ')' : '' }}</p>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                            <div v-if="viewingRequest.user">
                                <label class="text-sm font-semibold text-gray-700">User Name</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.user.name || 'N/A' }}</p>
                            </div>
                            <div v-if="viewingRequest.user">
                                <label class="text-sm font-semibold text-gray-700">Email</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.user.email || 'N/A' }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Student ID</label>
                                <p class="mt-1 text-gray-900">{{ viewingRequest.student_id }}</p>
                            </div>
                            <div>
                                <label class="text-sm font-semibold text-gray-700">Created At</label>
                                <p class="mt-1 text-gray-900">{{ formatDate(viewingRequest.created_at) }}</p>
                            </div>
                            <div v-if="role != 'user' && viewingRequest.student_id" class="col-span-2">
                                <button @click="viewStudentProfile(viewingRequest.student_id)"
                                    class="px-4 py-2 text-sm font-medium rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors flex items-center gap-2">
                                    <font-awesome-icon :icon="['fas', 'user']" />
                                    View Student Profile
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <!-- Student Profile Modal -->
        <transition name="fade" mode="out-in">
            <base-full-modal v-if="showStudentProfileFullModal" title="Student Profile" @close="closeStudentProfileModal">
                <student-profile :student-id="studentId"></student-profile>
            </base-full-modal>
        </transition>

        <!-- Preview Image Modal -->
        <transition name="fade" mode="out-in">
            <base-modal v-if="previewImageModal" title="" height="50vh" @close="closePreviewImageModal">
                <img class="h-full w-full object-contain" :src="previewImg" />
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import Pagination from '../../components/shared/pagination.vue';
import TableShimmer from '../../components/shared/TableShimmer.vue';
import EmploymentRequestFormModal from './components/EmploymentRequestFormModal.vue';
import StudentProfile from '../student-profile/index.vue';
import IconEye from '../../components/icons/IconEye.vue';
import IconPen from '../../components/icons/IconPen.vue';
import IconTrash from '../../components/icons/IconTrash.vue';
import xlsx from 'xlsx/dist/xlsx.full.min.js';

const LIST_EMPLOYMENT_REQUESTS_QUERY = gql`
    query EmploymentRequestsList(
        $first: Int!
        $page: Int!
        $search: String
        $isLookingForJob: Boolean
        $locations: [String!]
        $partnerIds: [ID!]
        $entity: String
        $campusCode: String
    ) {
        employmentRequests(
            first: $first
            page: $page
            search: $search
            is_looking_for_job: $isLookingForJob
            locations: $locations
            partner_ids: $partnerIds
            entity: $entity
            campus_code: $campusCode
        ) {
            error
            error_code
            message
            data {
                id
                is_looking_for_job
                academic_year
                semester
                preferred_location
                expected_salary
                additional_notes
                partners {
                    id
                    en_company_name
                    company_short_name
                }
                user {
                    id
                    name
                    email
                    profile_picture_url
                }
                student_id
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const EMPLOYMENT_REQUESTS_DASHBOARD_QUERY = gql`
    query EmploymentRequestsDashboard(
        $isLookingForJob: Boolean
        $locations: [String!]
        $partnerIds: [ID!]
        $entity: String
        $campusCode: String
    ) {
        employmentRequestsDashboard(
            is_looking_for_job: $isLookingForJob
            locations: $locations
            partner_ids: $partnerIds
            entity: $entity
            campus_code: $campusCode
        ) {
            error
            error_code
            message
            data {
                total_requests
                total_looking_for_job
                total_not_looking_for_job
            }
        }
    }
`;

export default {
    name: 'EmploymentRequestsIndex',
    components: {
        Multiselect,
        Pagination,
        TableShimmer,
        EmploymentRequestFormModal,
        StudentProfile,
        IconEye,
        IconPen,
        IconTrash,
    },
    data() {
        return {
            requestsList: [],
            loading: false,
            showCreateModal: false,
            editingRequest: null,
            viewingRequest: null,
            paginatorInfo: null,
            first: 10,
            page: 1,
            searchTerm: '',
            previewImageModal: false,
            previewImg: '',
            // Dashboard + filters
            showFilter: false,
            filters: {
                lookingForJob: '',
                selectedLocations: [],
                selectedEmployers: [],
                entity: '',
                campus: '',
            },
            entitiesList: [],
            campusList: [],
            dashboardStats: {
                totalRequests: 0,
                lookingForJobCount: 0,
                notLookingForJobCount: 0,
            },
            showStudentProfileFullModal: false,
            studentId: null,
            isExportExcel: false,
            isApiCallRecurisive: false,
            generatedReport: [],
        };
    },
    computed: {
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) {
                return null;
            }
            const firstItem = this.paginatorInfo.firstItem || ((this.paginatorInfo.currentPage - 1) * this.paginatorInfo.perPage + 1);
            const lastItem = this.paginatorInfo.lastItem || Math.min(this.paginatorInfo.currentPage * this.paginatorInfo.perPage, this.paginatorInfo.total);
            return {
                ...this.paginatorInfo,
                firstItem,
                lastItem,
            };
        },
        role() {
            return this.$store.getters.role;
        },
        currentUserId() {
            const userId = this.$store.getters.userId;
            if (userId) {
                return userId;
            }
            const user = this.$store.getters.user;
            if (user && user.id) {
                return user.id;
            }
            return null;
        },
        hasExistingRequestForCurrentUser() {
            if (!this.currentUserId || !this.requestsList || this.requestsList.length === 0) {
                return false;
            }
            return this.requestsList.some(
                (request) => request.user && request.user.id == this.currentUserId
            );
        },
        isLookingForJobFilterValue() {
            if (this.filters.lookingForJob === 'yes') return true;
            if (this.filters.lookingForJob === 'no') return false;
            return null;
        },
        availableLocations() {
            const locationSet = new Set();
            this.requestsList.forEach((request) => {
                if (request.preferred_location) {
                    locationSet.add(request.preferred_location);
                }
            });
            return Array.from(locationSet).map((loc) => ({
                label: loc,
                value: loc,
            }));
        },
        availableEmployers() {
            const employerMap = new Map();
            this.requestsList.forEach((request) => {
                if (request.partners && request.partners.length > 0) {
                    request.partners.forEach((partner) => {
                        if (partner && !employerMap.has(partner.id)) {
                            employerMap.set(partner.id, partner);
                        }
                    });
                }
            });
            return Array.from(employerMap.values());
        },
        filteredCampuses() {
            if (this.filters.entity) {
                return this.campusList.filter((c) => c.entity === this.filters.entity);
            }
            return this.campusList;
        },
    },
    watch: {
        'filters.entity'() {
            this.filters.campus = '';
            this.loadCampusList();
        },
    },
    created() {
        this.loadRequests();
        this.loadEntities();
        this.loadCampusList();
    },
    methods: {
        async loadEntities() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetEntitiesList {
                            entitiesList {
                                id
                                entity
                            }
                        }
                    `,
                    fetchPolicy: 'network-only',
                });
                this.entitiesList = data.entitiesList || [];
            } catch (err) {
                console.error('Failed to load entities:', err);
                this.entitiesList = [];
            }
        },
        async loadCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetCampusList($student_status: String, $entity: String) {
                            getCampusList(student_status: $student_status, entity: $entity) {
                                entity
                                campus_code
                            }
                        }
                    `,
                    variables: {
                        student_status: 'IG',
                        entity: this.filters.entity || null,
                    },
                    fetchPolicy: 'network-only',
                });
                const result = data.getCampusList;
                this.campusList = Array.isArray(result) ? result : [];
            } catch (err) {
                console.error('Failed to load campus list:', err);
                this.campusList = [];
            }
        },
        async loadRequests() {
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: LIST_EMPLOYMENT_REQUESTS_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        search: this.searchTerm || '',
                        isLookingForJob: this.isLookingForJobFilterValue,
                        locations: this.filters.selectedLocations.map((l) => l.value || l),
                        partnerIds: this.filters.selectedEmployers.map((p) => p.id),
                        entity: this.filters.entity || null,
                        campusCode: this.filters.campus || null,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.employmentRequests.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.employmentRequests.message,
                    });
                } else {
                    this.requestsList = data.employmentRequests.data;
                    this.paginatorInfo = data.employmentRequests.paginatorInfo;
                    // Load dashboard stats when requests are loaded
                    if (this.hasSuperPermission('employment-requests-dashboard')) {
                        await this.loadDashboardAnalytics();
                    }
                }
            } catch (error) {
                console.log(error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to load employment requests',
                });
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async loadDashboardAnalytics() {
            try {
                const { data } = await this.$apollo.query({
                    query: EMPLOYMENT_REQUESTS_DASHBOARD_QUERY,
                    variables: {
                        isLookingForJob: this.isLookingForJobFilterValue,
                        locations: this.filters.selectedLocations.map((l) => l.value || l),
                        partnerIds: this.filters.selectedEmployers.map((p) => p.id),
                        entity: this.filters.entity || null,
                        campusCode: this.filters.campus || null,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.employmentRequestsDashboard && !data.employmentRequestsDashboard.error) {
                    const stats = data.employmentRequestsDashboard.data || {};
                    this.dashboardStats.totalRequests = stats.total_requests || 0;
                    this.dashboardStats.lookingForJobCount = stats.total_looking_for_job || 0;
                    this.dashboardStats.notLookingForJobCount = stats.total_not_looking_for_job || 0;
                    return;
                }
            } catch (error) {
                console.error('Failed to load employment requests dashboard analytics:', error);
            }

            // Fallback: compute basic stats from currently loaded list (filtered by current filters)
            const filtered = this.requestsList.filter((request) => {
                // Looking for job filter
                if (this.isLookingForJobFilterValue === true && !request.is_looking_for_job) return false;
                if (this.isLookingForJobFilterValue === false && request.is_looking_for_job) return false;

                // Location filter
                const selectedLocationValues = this.filters.selectedLocations.map((l) => l.value || l);
                if (selectedLocationValues.length > 0) {
                    if (!request.preferred_location || !selectedLocationValues.includes(request.preferred_location)) {
                        return false;
                    }
                }

                // Employer filter
                const selectedEmployerIds = this.filters.selectedEmployers.map((p) => p.id);
                if (selectedEmployerIds.length > 0) {
                    const requestPartnerIds = (request.partners || []).map((p) => p.id);
                    const hasAny = selectedEmployerIds.some((id) => requestPartnerIds.includes(id));
                    if (!hasAny) return false;
                }

                return true;
            });

            this.dashboardStats.totalRequests = filtered.length;
            this.dashboardStats.lookingForJobCount = filtered.filter((r) => r.is_looking_for_job).length;
            this.dashboardStats.notLookingForJobCount = filtered.filter((r) => !r.is_looking_for_job).length;
        },
        setPage(page) {
            this.page = page;
            this.loadRequests();
        },
        onSearch(searchTerm) {
            this.searchTerm = searchTerm;
            this.page = 1;
            this.loadRequests();
        },
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.filters = {
                lookingForJob: '',
                selectedLocations: [],
                selectedEmployers: [],
                entity: '',
                campus: '',
            };
            this.page = 1;
            this.loadRequests();
        },
        applyFilters() {
            this.showFilter = false;
            this.page = 1;
            this.loadRequests();
        },
        partnerLabel(partner) {
            if (!partner) return '';
            return partner.en_company_name || partner.company_short_name || 'Unknown Employer';
        },
        getEntityDisplayName(entity) {
            if (entity === 'ATHS') return 'ATS';
            return entity || '';
        },
        viewRequest(request) {
            this.viewingRequest = request;
        },
        editRequest(request) {
            this.editingRequest = request;
        },
        async deleteRequest(request) {
            const result = await this.$swal.fire({
                title: 'Are you sure?',
                text: `You are about to delete this employment request. This action cannot be undone!`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            });

            if (!result.isConfirmed) {
                return;
            }

            try {
                const { data } = await this.$apollo.mutate({
                    mutation: gql`
                        mutation DeleteEmploymentRequest($id: ID!) {
                            deleteEmploymentRequest(id: $id) {
                                error
                                error_code
                                message
                            }
                        }
                    `,
                    variables: {
                        id: request.id,
                    },
                });

                if (!data || !data.deleteEmploymentRequest) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Failed!',
                        text: 'Failed to delete request: Invalid response',
                    });
                    return;
                }

                if (data.deleteEmploymentRequest.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: data.deleteEmploymentRequest.message || 'Failed to delete request',
                    });
                } else {
                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Request deleted successfully',
                        showConfirmButton: false,
                        timer: 1000,
                        timerProgressBar: true,
                    });
                    this.loadRequests();
                }
            } catch (error) {
                console.error('Delete request error:', error);
                let errorMessage = 'Failed to delete request';

                if (error.graphQLErrors && error.graphQLErrors.length > 0 && error.graphQLErrors[0].message) {
                    errorMessage = error.graphQLErrors[0].message;
                } else if (error.networkError && error.networkError.message) {
                    errorMessage = error.networkError.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: errorMessage,
                });
            }
        },
        closeModal() {
            this.showCreateModal = false;
            this.editingRequest = null;
        },
        closeViewModal() {
            this.viewingRequest = null;
        },
        viewStudentProfile(studentId) {
            this.studentId = studentId;
            this.showStudentProfileFullModal = true;
        },
        closeStudentProfileModal() {
            this.showStudentProfileFullModal = false;
            this.studentId = null;
        },
        handleRequestSaved() {
            this.closeModal();
            this.loadRequests();
        },
        async generateReport() {
            let page = 1;
            const limit = 200;
            this.isExportExcel = true;
            this.isApiCallRecurisive = true;
            while (this.isApiCallRecurisive) {
                const responseData = await this.fetchForExporting(page, limit);
                if (responseData && responseData.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: responseData.message || 'Failed to export',
                    });
                    break;
                }
                const items = responseData && responseData.data ? responseData.data : [];
                if (items.length > 0) {
                    this.generatedReport.push(
                        ...items.map((obj) => {
                            const partners = obj.partners && obj.partners.length > 0
                                ? obj.partners.map((p) => p.en_company_name || p.company_short_name || '').filter(Boolean).join(', ')
                                : '';
                            return {
                                name: obj.user && obj.user.name ? obj.user.name : '',
                                email: obj.user && obj.user.email ? obj.user.email : '',
                                student_id: obj.student_id || '',
                                looking_for_job: obj.is_looking_for_job === true ? 'Yes' : 'No',
                                academic_year: obj.academic_year || '',
                                semester: obj.semester || '',
                                preferred_location: obj.preferred_location || '',
                                expected_salary: obj.expected_salary || '',
                                preferred_employers: partners,
                                additional_notes: obj.additional_notes || '',
                                created_at: this.formatDate(obj.created_at),
                            };
                        })
                    );
                    page++;
                } else {
                    this.isApiCallRecurisive = false;
                    break;
                }
            }
            this.exportToExcel('Employment_Requests');
        },
        async fetchForExporting(pageNo = 1, limitSize = 200) {
            try {
                const { data } = await this.$apollo.query({
                    query: LIST_EMPLOYMENT_REQUESTS_QUERY,
                    variables: {
                        first: limitSize,
                        page: pageNo,
                        search: this.searchTerm || '',
                        isLookingForJob: this.isLookingForJobFilterValue,
                        locations: this.filters.selectedLocations.map((l) => l.value || l),
                        partnerIds: this.filters.selectedEmployers.map((p) => p.id),
                        entity: this.filters.entity || null,
                        campusCode: this.filters.campus || null,
                    },
                    fetchPolicy: 'network-only',
                });
                return data.employmentRequests;
            } catch (error) {
                console.error('Export fetch error:', error);
                return { error: true, message: error.message || 'Failed to fetch data' };
            }
        },
        exportToExcel(reportName) {
            const XLSX = xlsx;
            const header = [
                'NAME',
                'EMAIL',
                'STUDENT ID',
                'LOOKING FOR JOB',
                'ACADEMIC YEAR',
                'SEMESTER',
                'PREFERRED LOCATION',
                'EXPECTED SALARY',
                'PREFERRED EMPLOYERS',
                'ADDITIONAL NOTES',
                'CREATED AT',
            ];
            const worksheet = XLSX.utils.json_to_sheet(this.generatedReport);
            const columnWidths = header.map((h) => ({ width: Math.max(15, h.length + 5) }));
            worksheet['!cols'] = columnWidths;
            for (let i = 0; i < header.length; i++) {
                const col = String.fromCharCode(65 + (i % 26));
                const row = Math.floor(i / 26);
                const colLetter = row > 0 ? String.fromCharCode(64 + row) + col : col;
                worksheet[`${colLetter}1`] = { v: header[i], t: 's' };
            }
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
            XLSX.writeFile(workbook, `${reportName}.xlsx`);
            this.generatedReport = [];
            this.isExportExcel = false;
            this.isApiCallRecurisive = false;
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        getProfilePicture(profilePictureUrl) {
            if (profilePictureUrl) {
                if (
                    profilePictureUrl ==
                    "/storage/alumni-profile-pictures/default-avatar.jpg"
                ) {
                    // If default avatar, try to get from SIS - we'll use a generic entity or fetch entity separately if needed
                    // For now, return the default avatar from public URL
                    return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                } else {
                    return process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                }
            } else {
                // If no profile picture, return default avatar
                return process.env.VUE_APP_PUBLIC_URL + '/storage/alumni-profile-pictures/default-avatar.jpg';
            }
        },
        openPreviewImgModal(profilePictureUrl) {
            if (profilePictureUrl) {
                if (
                    profilePictureUrl ==
                    "/storage/alumni-profile-pictures/default-avatar.jpg"
                ) {
                    this.previewImg = process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                } else {
                    this.previewImg = process.env.VUE_APP_PUBLIC_URL + profilePictureUrl;
                }
            } else {
                this.previewImg = process.env.VUE_APP_PUBLIC_URL + '/storage/alumni-profile-pictures/default-avatar.jpg';
            }
            this.previewImageModal = true;
        },
        closePreviewImageModal() {
            this.previewImageModal = false;
            this.previewImg = "";
        },
        hasSuperPermission(crudName) {
            const permissions = this.$store.getters.permissions;
            if (!permissions) {
                return false;
            }
            const permissionsArray = permissions.split(",");
            if (permissionsArray.includes(crudName)) {
                return true;
            } else {
                return false;
            }
        },
        canUpdateRequest(request) {
            // Users with 'employment-requests-update' can update any request OR their own request
            if (!this.hasSuperPermission('employment-requests-update')) {
                return false;
            }
            // Check if it's the user's own request
            if (request.user && request.user.id == this.currentUserId) {
                return true;
            }
            // User has permission, so they can update any request
            return true;
        },
        canDeleteRequest(request) {
            // Users with 'employment-requests-delete' can delete any request OR their own request
            if (!this.hasSuperPermission('employment-requests-delete')) {
                return false;
            }
            // Check if it's the user's own request
            if (request.user && request.user.id == this.currentUserId) {
                return true;
            }
            // User has permission, so they can delete any request
            return true;
        },
    },
};
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}
</style>
